// @greenlight-spec: 28-it-dashboard-ui.md
// @greenlight-spec: 19-design-system.md
import * as React from 'react';
import { Shield } from 'lucide-react';

import { MonogramTile } from '@/components/greenlight/MonogramTile';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { initials } from '@/lib/hueFor';
import type { UserListItem } from '@greenlight/shared';

export interface AppAccessClusterProps {
  user: UserListItem;
}

const MAX_TILES = 4;

/**
 * Summarises a user's app access as a row of monogram tiles with overflow count.
 * Renders text-only variants for deactivated, IT (admin / org_admin), and empty states.
 */
export function AppAccessCluster({ user }: AppAccessClusterProps): React.JSX.Element {
  if (!user.is_active) {
    return (
      <span className="font-mono text-[12px] text-muted-foreground whitespace-nowrap">
        No access
      </span>
    );
  }

  if (user.role === 'org_admin' || user.role === 'admin') {
    return (
      <span className="inline-flex items-center gap-1.5 text-primary">
        <Shield className="h-3.5 w-3.5" aria-hidden />
        <span className="text-[12.5px] font-medium whitespace-nowrap">All apps</span>
      </span>
    );
  }

  if (user.accessible_apps == null) {
    return <span className="font-mono text-[12px] text-muted-foreground whitespace-nowrap">—</span>;
  }

  if (user.accessible_apps.length === 0) {
    return (
      <span className="font-mono text-[12px] text-muted-foreground whitespace-nowrap">No apps</span>
    );
  }

  const apps = user.accessible_apps;
  const visible = apps.slice(0, MAX_TILES);
  const overflow = apps.length - MAX_TILES;

  return (
    <span className="inline-flex items-center gap-1">
      {visible.map((app) => (
        <Tooltip key={app.app_id}>
          <TooltipTrigger asChild>
            {/* Not focusable: the row is a button, so a focusable descendant here
                would be a nested-interactive a11y violation. The app names are
                fully listed in the inspector that opens on row activation. */}
            <span className="cursor-default">
              <MonogramTile
                seed={app.app_slug}
                label={initials(app.app_name)}
                size={24}
                shape="rounded"
              />
            </span>
          </TooltipTrigger>
          <TooltipContent>{app.app_name}</TooltipContent>
        </Tooltip>
      ))}
      {overflow > 0 && (
        <span className="inline-flex items-center justify-center h-6 px-1.5 rounded-full bg-muted font-mono text-[11px] text-foreground whitespace-nowrap tabular-nums">
          +{overflow}
        </span>
      )}
      <span className="font-mono text-[11.5px] text-muted-foreground whitespace-nowrap tabular-nums ml-0.5">
        {apps.length} {apps.length === 1 ? 'app' : 'apps'}
      </span>
    </span>
  );
}
